import { useEffect, useState } from "react";
import axios from "axios";
import { ArrowRight, Loader2 } from "lucide-react";
import { API } from "@/lib/api";

axios.defaults.withCredentials = true;

function GateScreen({ onUnlock }) {
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [shake, setShake] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!code || busy) return;
    setBusy(true);
    setErr("");
    try {
      await axios.post(`${API}/access/unlock`, { code });
      onUnlock();
    } catch (x) {
      setErr(x?.response?.status === 429 ? "Too many attempts. Try again in 15 minutes." : "That code isn't right.");
      setCode("");
      setShake(true);
      setTimeout(() => setShake(false), 450);
    } finally {
      setBusy(false);
    }
  };

  return (
    <main data-testid="access-gate" className="gate-root min-h-screen flex items-center justify-center px-6">
      <div className="gate-glow" aria-hidden />
      <form onSubmit={submit} className="relative w-full max-w-sm text-center gate-rise">
        <h1 data-testid="gate-title" className="gate-serif text-6xl sm:text-7xl text-[#efe7d6] tracking-wide">Waiholo</h1>
        <p data-testid="gate-subtitle" className="mt-5 text-[11px] uppercase tracking-[0.45em] text-[#8a93a6]">Code required</p>
        <div className={`mt-12 flex items-center border-b border-[#2a3242] focus-within:border-[#c9a96e] transition-colors duration-300 ${shake ? "gate-shake" : ""}`}>
          <input data-testid="gate-code-input" type="password" inputMode="numeric" pattern="[0-9]*" autoComplete="one-time-code" autoFocus
            maxLength={12} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} aria-label="Access code"
            className="flex-1 bg-transparent py-3 text-center text-2xl tracking-[0.6em] text-[#efe7d6] placeholder:text-[#3a4252] outline-none font-mono-num"
            placeholder="••••" />
          <button data-testid="gate-submit-btn" type="submit" disabled={!code || busy} aria-label="Enter"
            className="p-2 text-[#c9a96e] disabled:text-[#3a4252] hover:translate-x-0.5 transition-transform duration-200">
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <ArrowRight className="w-5 h-5" />}
          </button>
        </div>
        <p data-testid="gate-error" role="alert" className={`mt-4 h-4 text-xs text-[#d58b8b] transition-opacity ${err ? "opacity-100" : "opacity-0"}`}>{err}</p>
        <p data-testid="gate-mahalo" className="gate-serif italic mt-16 text-lg text-[#6f7889]">Mahalo</p>
      </form>
    </main>
  );
}

export default function AccessGate({ children }) {
  const [unlocked, setUnlocked] = useState(null);
  useEffect(() => {
    axios.get(`${API}/access/status`).then((r) => setUnlocked(!!r.data.unlocked)).catch(() => setUnlocked(false));
    const id = axios.interceptors.response.use((r) => r, (e) => {
      if (e?.response?.status === 401 && !String(e.config?.url || "").includes("/access/")) setUnlocked(false);
      return Promise.reject(e);
    });
    return () => axios.interceptors.response.eject(id);
  }, []);
  if (unlocked === null) return <div data-testid="gate-loading" className="gate-root min-h-screen" />;
  return unlocked ? children : <GateScreen onUnlock={() => setUnlocked(true)} />;
}
